import { cn, hasMessage } from '@/lib/utils';
import { MutableRefObject } from 'react';

import { FileSpec, useChatData, useChatMessages } from '@chainlit/react-client';

import WaterMark from '@/components/WaterMark';

import MemoryNudge from './Memory/MemoryNudge';
import MessageComposer from './MessageComposer';
import PendingAskBar from './PendingAskBar';

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
  showIfEmptyThread?: boolean;
}

export default function ChatFooter({ showIfEmptyThread, ...props }: Props) {
  const { messages } = useChatMessages();
  const { reconnecting } = useChatData();
  if (!hasMessage(messages) && !showIfEmptyThread) return null;

  return (
    <div className={cn('relative flex flex-col items-center gap-2 w-full')}>
      {/* Anchored to this already-relative footer, so a pending question stays
          in front of the input no matter where the user has scrolled. */}
      <PendingAskBar />
      {/* The socket dropped and socket.io is reconnecting. The composer
          does not send meanwhile (useChatData.connected); what is typed stays
          in it. English always: this is the system talking. */}
      {reconnecting ? (
        <div
          role="status"
          className="flex items-center gap-2 text-[12.5px] text-muted-foreground"
        >
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground/25 border-t-muted-foreground" />
          Reconnecting…
        </div>
      ) : null}
      <MemoryNudge />
      <MessageComposer {...props} />
      <WaterMark />
    </div>
  );
}
